import { createContext, useCallback, useEffect, useState } from 'react';
import type { User, Role } from '@altyor-kronos/shared-types';
import { authClient } from './authClient';
import { decodeClaims } from './tokenClaims';
import { apiPost, ApiError } from '../data/restClient';

export interface AuthAccount {
  oid: string;
  name: string;
  email: string;
}

export interface AuthState {
  account: AuthAccount | null;
  user: User | null;
  loading: boolean;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  acquireToken: () => Promise<string>;
  /**
   * Patch the in-memory profile. The REST profile is a one-time fetch with no
   * live listener, so a write to `users/{uid}` (favourites, hidden projects,
   * prefs) is invisible until a full page reload unless the caller mirrors it
   * here. Callers patch optimistically and revert on failure — a round-trip is
   * too slow for a star toggle over the China path.
   */
  updateUser: (patch: Partial<User>) => void;
}

export const AuthContext = createContext<AuthState>({
  account: null,
  user: null,
  loading: true,
  login: async () => {},
  logout: async () => {},
  acquireToken: async () => '',
  updateUser: () => {},
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [account, setAccount] = useState<AuthAccount | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const logout = useCallback(async () => {
    await authClient.logout();
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await authClient.ready();
      await authClient.handleRedirect();
      if (!authClient.hasAccount()) {
        if (!cancelled) setLoading(false);
        return;
      }
      try {
        const token = await authClient.acquireToken();
        const claims = decodeClaims(token);
        if (cancelled) return;
        setAccount({ oid: claims.oid, name: claims.name, email: claims.email });
        // Bootstrap du profil en un seul appel : provision est idempotent et
        // renvoie le profil qu'il l'ait créé ou trouvé. Le GET préalable n'était
        // là que pour décider s'il fallait provisionner — il ne peut plus servir
        // à ça depuis le garde-fou de compte, qui répond 403 (et non 404) à un
        // token sans doc, donc le 404 qui déclenchait le provisioning n'arrive
        // plus jamais. Un 403 ici veut dire refusé : RequireAuth affiche alors
        // « compte non provisionné / contactez un administrateur ».
        const profile = await apiPost<User>('/users/provision', {});
        if (cancelled) return;
        // Rôles du token font foi (spec §2.3).
        setUser({ ...profile, roles: claims.roles as Role[] });
      } catch (e) {
        if (cancelled) return;
        if (e instanceof ApiError && e.status === 401) {
          await authClient.logout(); // token invalide/expiré → jamais d'état à demi-loggé
          return;
        }
        // 404 (profil absent) ou 5xx : on laisse account posé + user null.
        // RequireAuth affiche « compte non provisionné » ; on ne swallow pas l'erreur.
        console.error('profile bootstrap failed:', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(() => authClient.login(), []);
  const acquireToken = useCallback(() => authClient.acquireToken(), []);
  const updateUser = useCallback(
    (patch: Partial<User>) => setUser((prev) => (prev ? { ...prev, ...patch } : prev)),
    [],
  );

  return (
    <AuthContext.Provider value={{ account, user, loading, login, logout, acquireToken, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
}
